import { GLOBAL_SEARCH_MIN_QUERY_LENGTH } from "@frontend-shared/constants";
import { Search } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { GLOBAL_SEARCH_TAB_LABEL_KEYS } from "./constants";
import {
  EGlobalSearchTabStatus,
  type IGlobalSearchTabMessageProps,
} from "./types";

// NOTE: NSAI-6853 — the one line a category tab shows in place of results:
// before the user types (on a tab with nothing to browse), while the query is
// too short to send, and once every source has answered with nothing.
const GlobalSearchTabMessageComponent = ({
  status,
  tab,
  query,
}: IGlobalSearchTabMessageProps): JSX.Element | null => {
  const { t } = useTranslation("globalSearch");

  if (status === EGlobalSearchTabStatus.NONE) return null;

  const category = t(GLOBAL_SEARCH_TAB_LABEL_KEYS[tab]);
  const messages: Record<
    Exclude<EGlobalSearchTabStatus, EGlobalSearchTabStatus.NONE>,
    string
  > = {
    [EGlobalSearchTabStatus.IDLE]: t("tab_idle", { category }),
    [EGlobalSearchTabStatus.MIN_LENGTH]: t("min_length_hint", {
      count: GLOBAL_SEARCH_MIN_QUERY_LENGTH,
    }),
    [EGlobalSearchTabStatus.EMPTY]: t("tab_empty", {
      category,
      query: query.trim(),
    }),
  };

  return (
    <div
      role="status"
      className="flex h-full min-h-48 flex-col items-center justify-center gap-3 px-6 text-center"
      data-testid={`global-search-tab-status-${status}`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-glass-fill text-foreground-secondary">
        <Search className="h-5 w-5" />
      </span>
      <p className="max-w-xs text-sm text-foreground-secondary">
        {messages[status]}
      </p>
    </div>
  );
};

export const GlobalSearchTabMessage = memo(GlobalSearchTabMessageComponent);
